/**
 * C3b · P2 — Shared DD-MMM-YYYY date picker.
 *
 * Extracted from `pages/Misstatements.jsx` (it lived there since F7).
 * Kept the exact API, so every call site — Misstatements W9 rows,
 * signoff widgets, and the new Tcwg311aLog row editor — sees the same
 * component with the same test-id behaviour.
 *
 *   <DateField value="2026-09-29"
 *              onCommit={(ymd) => …}       // "" when cleared
 *              placeholder="DD-MMM-YYYY"    // shown when value is empty
 *              testid="…" />
 *
 * Displays as an LTR-isolated "29-Sep-2026" so Arabic layouts never
 * scramble the digits.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { arSA } from "date-fns/locale";
// S5 — bring `useI18n` in so the calendar can show Arabic month +
// day names on the AR screen (digits stay Latin per Batch C · Fix
// List 7 Q5 — DD-MMM-YYYY LTR-isolated).
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/dt";   // STEP 3 · B-7c: the one formatter
import { parseTypedDate } from "@/components/DateInput";   // Step 11c-3 · find 32: the house typed-date parser

/** Parse "YYYY-MM-DD" (or ISO) → Date without timezone drift. */
export function parseYmd(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (!m) { const d = new Date(s); return isNaN(d.getTime()) ? null : d; }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Format Date/ISO → "29-Sep-2026", wrapped in U+2068..U+2069 (LTR isolate). */
export function fmtYmd(s) {
  if (!s) return "";
  const out = fmtDate(s instanceof Date ? toYmd(s) : String(s));   // a picked Date is a calendar day, no zone shift
  return /\d{4}\u2069$/.test(out) ? out : "";
}

/** Format Date → "YYYY-MM-DD" for persistence. */
export function toYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Step 11c-3 · find 32 (Mars live, W9): the field was a calendar only — no typed entry, Oct-2026 → Feb-2026 took 8 clicks per row,
// and the pop-over moved as month heights changed. Now the box takes typed DD-MMM-YYYY (also 05/02/2026, 2026-02-05, an Arabic
// month name — the house DateInput parser, day first, never month first) and the calendar button stays; the calendar keeps six
// week rows (fixedWeeks) so it does not move between months. The value stays YYYY-MM-DD; onCommit gets it ("" when cleared).
export default function DateField({ value, onCommit, placeholder, testid, className }) {
  const [open, setOpen] = useState(false);
  const { lang } = useI18n();
  const selected = parseYmd(value);
  const shown = value ? String(fmtYmd(value)).replace(/[\u2066-\u2069]/g, "") : "";
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  useEffect(() => { setText(shown); setBad(false); }, [shown]);
  const commitText = () => {
    const raw = text.trim();
    if (!raw) { setBad(false); if (value) onCommit(""); return; }
    const iso = parseTypedDate(raw);
    if (!iso) { setBad(true); return; }
    setBad(false);
    setText(String(fmtYmd(iso)).replace(/[\u2066-\u2069]/g, ""));
    if (iso !== String(value || "").slice(0, 10)) onCommit(iso);
  };
  return (
    <span className={"inline-flex items-center gap-1 " + (className || "")} data-testid={testid ? `${testid}-wrap` : undefined}>
      <input type="text" dir="ltr" inputMode="numeric" value={text} placeholder={placeholder || "DD-MMM-YYYY"}
             onChange={(e) => { setText(e.target.value); setBad(false); }} onBlur={commitText}
             onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
             className={`h-7 w-[118px] rounded border px-2 font-mono text-[11px] ${bad ? "border-red-500" : ""}`}
             aria-invalid={bad || undefined}
             title={bad ? (lang === "ar" ? "تاريخ غير صالح — اكتبه بصيغة DD-MMM-YYYY (مثل 05-Feb-2026)" : "Not a date — type it as DD-MMM-YYYY (for example 05-Feb-2026)") : undefined}
             data-testid={testid ? `${testid}-text` : undefined} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" type="button" className="h-7 px-1.5"
                  aria-label={lang === "ar" ? "اختر من التقويم" : "Pick from the calendar"} data-testid={testid}>
            <CalendarIcon className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected || undefined}
            defaultMonth={selected || undefined}
            fixedWeeks
            onSelect={(d) => { setOpen(false); onCommit(toYmd(d) || ""); }}
            initialFocus
            locale={lang === "ar" ? arSA : undefined}
            dir={lang === "ar" ? "rtl" : "ltr"}
            data-testid={testid ? `${testid}-cal` : undefined}
          />
        </PopoverContent>
      </Popover>
      {bad && <span className="text-[10px] text-red-600" data-testid={testid ? `${testid}-error` : undefined}>DD-MMM-YYYY</span>}
    </span>
  );
}
